import { MainLayout } from '@/components/layout/MainLayout';

export const PrivacyPage = () => {
  return (
    <MainLayout>
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-14 md:top-0 z-20 border-b border-gray-200 px-4 py-4 md:px-8 md:py-5 bg-gray-50/80 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 rounded-md p-2">
              <span className="material-symbols-outlined text-primary text-2xl">shield</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Privacy & Data Safety</h2>
              <p className="text-sm text-gray-500">How OchrePoint handles your information</p>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-8">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Trust Banner */}
            <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl flex items-center gap-4">
              <span className="material-symbols-outlined text-green-600 text-4xl flex-shrink-0">verified_user</span>
              <div>
                <p className="font-semibold text-green-900 mb-1">Your data is safe with us</p>
                <p className="text-sm text-green-800">OchrePoint Contracting is a local Duncan, BC business. Your data stays on our secure servers and is never sold to third parties.</p>
              </div>
            </div>

            {/* Sections */}
            <Section icon="storage" title="What Data We Collect">
              <p>We collect only the information needed to run your construction projects:</p>
              <ul>
                <li><strong>Account information:</strong> Name, email address, and password (stored as a secure hash — we never see your plain-text password).</li>
                <li><strong>Business data:</strong> Job details, client information, employee records, invoices, quotes, and documents you create in the platform.</li>
                <li><strong>Uploaded files:</strong> Documents, photos, and attachments you upload to jobs and invoices.</li>
                <li><strong>Usage data:</strong> Login timestamps and basic activity logs for security monitoring.</li>
              </ul>
            </Section>

            <Section icon="lock" title="How We Protect Your Data">
              <ul>
                <li><strong>Encryption in transit:</strong> All data sent between your browser and our servers is encrypted using HTTPS/TLS.</li>
                <li><strong>Password hashing:</strong> Passwords are hashed with bcrypt. We cannot retrieve your password — only reset it.</li>
                <li><strong>JWT authentication:</strong> Secure, short-lived tokens authenticate every API request. Tokens expire and require refresh.</li>
                <li><strong>Server security:</strong> Our servers run on hardened Linux infrastructure with firewalls and regular security updates.</li>
                <li><strong>File storage:</strong> Uploaded files are stored securely with access controls — only authorized users can access them.</li>
              </ul>
            </Section>

            <Section icon="share" title="Who Can See Your Data">
              <ul>
                <li><strong>Your admin/owner account:</strong> Has full access to all business data.</li>
                <li><strong>Staff users:</strong> See data relevant to their role. They cannot access finance settings or delete records.</li>
                <li><strong>Clients (via portal):</strong> Can only see their own jobs, documents, and invoices — not other clients' data.</li>
                <li><strong>Employees (via portal):</strong> Can only see their own schedule, timesheets, and shared documents — not financial data.</li>
                <li><strong>OchrePoint Contracting staff:</strong> The business owner and platform maintainer may access data for support purposes only.</li>
                <li><strong>Third parties:</strong> We do not sell, share, or rent your data to any third party. Integrations (like QuickBooks) only receive data you explicitly sync.</li>
              </ul>
            </Section>

            <Section icon="cloud" title="Data Storage & Retention">
              <ul>
                <li><strong>Location:</strong> Data is stored on servers in Canada.</li>
                <li><strong>Retention:</strong> Your data is retained for the duration of your account. When you close your account, data is deleted within 30 days.</li>
                <li><strong>Backups:</strong> Regular automated backups ensure data recovery in case of hardware failure. Backup data is subject to the same security controls.</li>
              </ul>
            </Section>

            <Section icon="person_off" title="Your Rights">
              <ul>
                <li><strong>Access:</strong> You can view all your data within the platform at any time.</li>
                <li><strong>Correction:</strong> You can update your information in Settings or by contacting us.</li>
                <li><strong>Deletion:</strong> You may request deletion of your account and data. Contact us at admin@ochrepoint.ca.</li>
                <li><strong>Portability:</strong> You can export job data, invoices, and reports from within the platform.</li>
              </ul>
            </Section>

            <Section icon="cookie" title="Cookies & Tracking">
              <ul>
                <li><strong>Authentication cookies:</strong> We use browser localStorage to store JWT tokens for keeping you logged in.</li>
                <li><strong>No ad tracking:</strong> We do not use advertising cookies, analytics platforms, or any third-party tracking.</li>
                <li><strong>No third-party scripts:</strong> No Google Analytics, Facebook Pixel, or similar trackers run on this platform.</li>
              </ul>
            </Section>

            <Section icon="integration_instructions" title="Third-Party Integrations">
              <ul>
                <li><strong>QuickBooks Online:</strong> If connected, client and invoice data is synced to Intuit's servers. Their privacy policy applies to data stored in QuickBooks.</li>
                <li><strong>Google (OAuth):</strong> If you log in with Google, your email is shared with us for authentication only. We don't access your Gmail or other Google data.</li>
                <li><strong>Email delivery:</strong> Invoice and invite emails may be sent through a third-party email service. Email content is not retained by the service beyond delivery.</li>
              </ul>
            </Section>

            <Section icon="edit_note" title="Changes to This Policy">
              <p>If we change how we handle your data, we'll notify account owners by email. The effective date of this policy is January 1, 2026.</p>
            </Section>

            {/* Contact */}
            <div className="p-6 bg-gray-50 border border-gray-200 rounded-2xl">
              <div className="flex items-center gap-3 mb-3">
                <span className="material-symbols-outlined text-primary">contact_support</span>
                <h3 className="font-semibold text-gray-900">Questions or Concerns?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                If you have any questions about how your data is handled, or want to request access, correction, or deletion:
              </p>
              <p className="text-sm">
                <strong>Email:</strong> <a href="mailto:admin@ochrepoint.ca" className="text-primary hover:underline">admin@ochrepoint.ca</a><br />
                <strong>Business:</strong> OchrePoint Contracting LTD<br />
                <strong>Location:</strong> Duncan & North Cowichan, BC, Canada
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

function Section({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <span className="material-symbols-outlined text-primary">{icon}</span>
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="px-6 py-5 text-sm text-gray-700 leading-relaxed space-y-2 [&_ul]:space-y-2 [&_ul]:list-none [&_li]:flex [&_li]:gap-2 [&_li]:before:content-['·'] [&_li]:before:text-primary [&_li]:before:font-bold">
        {children}
      </div>
    </div>
  );
}
